import React, { useEffect, useState } from 'react';

const DescopeDebugger = () => {
  const [debugInfo, setDebugInfo] = useState({
    projectId: '',
    networkTest: 'pending',
    descopeStatus: 'pending',
    browserInfo: {},
    errorDetails: null
  });

  useEffect(() => {
    const runDiagnostics = async () => {
      const projectId = process.env.REACT_APP_DESCOPE_PROJECT_ID;
      
      setDebugInfo(prev => ({
        ...prev,
        projectId: projectId || 'NOT SET',
        browserInfo: {
          userAgent: navigator.userAgent,
          location: window.location.origin,
          localStorage: typeof Storage !== "undefined" ? 'Available' : 'Not Available',
          cookies: navigator.cookieEnabled ? 'Enabled' : 'Disabled'
        }
      }));

      // Test basic network connectivity
      try {
        const response = await fetch('https://httpbin.org/ip');
        const data = await response.json();
        setDebugInfo(prev => ({
          ...prev,
          networkTest: `Success - Your IP: ${data.origin}`
        }));
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          networkTest: `Failed - ${error.message}`,
          errorDetails: error
        }));
      }

      // Test Descope API connectivity
      try {
        const descopeUrl = `https://api.descope.com/v1/mgmt/project`;
        const response = await fetch(descopeUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        setDebugInfo(prev => ({
          ...prev,
          descopeStatus: `Response: ${response.status} ${response.statusText}`
        }));
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          descopeStatus: `Failed - ${error.message}`,
          errorDetails: error
        }));
      }
    };

    runDiagnostics();
  }, []);

  const testDescopeSDK = async () => {
    try {
      // Try to initialize Descope SDK directly
      const { createSdk } = await import('@descope/web-js-sdk');
      const sdk = createSdk({
        projectId: process.env.REACT_APP_DESCOPE_PROJECT_ID,
        baseUrl: process.env.REACT_APP_DESCOPE_BASE_URL
      });
      
      // Test a simple SDK call
      const result = await sdk.me();
      setDebugInfo(prev => ({
        ...prev,
        sdkTest: 'SDK initialized successfully',
        sdkResult: JSON.stringify(result, null, 2)
      }));
    } catch (error) {
      setDebugInfo(prev => ({
        ...prev,
        sdkTest: `SDK Error: ${error.message}`,
        errorDetails: error
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Descope Diagnostics</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Project Configuration */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Project Configuration</h2>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm"><strong>Project ID:</strong></p>
                <p className="text-xs text-gray-600 break-all">{debugInfo.projectId}</p>
                <p className="text-sm mt-2"><strong>Base URL:</strong></p>
                <p className="text-xs text-gray-600">{process.env.REACT_APP_DESCOPE_BASE_URL || 'Not set'}</p>
              </div>
            </div>

            {/* Network Tests */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Network Tests</h2>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm"><strong>Basic Network:</strong></p>
                <p className="text-xs text-gray-600">{debugInfo.networkTest}</p>
                <p className="text-sm mt-2"><strong>Descope API:</strong></p>
                <p className="text-xs text-gray-600">{debugInfo.descopeStatus}</p>
              </div>
            </div>

            {/* Browser Info */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Browser Environment</h2>
              <div className="bg-gray-50 p-4 rounded text-xs">
                <p><strong>Origin:</strong> {debugInfo.browserInfo.location}</p>
                <p><strong>LocalStorage:</strong> {debugInfo.browserInfo.localStorage}</p>
                <p><strong>Cookies:</strong> {debugInfo.browserInfo.cookies}</p>
                <p><strong>User Agent:</strong> {debugInfo.browserInfo.userAgent?.substring(0, 100)}...</p>
              </div>
            </div>

            {/* Error Details */}
            {debugInfo.errorDetails && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-red-800">Error Details</h2>
                <div className="bg-red-50 p-4 rounded">
                  <pre className="text-xs text-red-700 whitespace-pre-wrap">
                    {JSON.stringify(debugInfo.errorDetails, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* SDK Test Button */}
          <div className="mt-6">
            <button
              onClick={testDescopeSDK}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Test Descope SDK Directly
            </button>
            {debugInfo.sdkTest && (
              <div className="mt-4 bg-gray-50 p-4 rounded">
                <p className="text-sm font-medium">{debugInfo.sdkTest}</p>
                {debugInfo.sdkResult && (
                  <pre className="text-xs mt-2 text-gray-600">{debugInfo.sdkResult}</pre>
                )}
              </div>
            )}
          </div>

          {/* Recommendations */}
          <div className="mt-6 bg-yellow-50 p-4 rounded">
            <h3 className="text-md font-semibold text-yellow-800 mb-2">Troubleshooting Steps:</h3>
            <ol className="text-sm text-yellow-700 list-decimal list-inside space-y-1">
              <li>Verify your Project ID is correct in Descope Console</li>
              <li>Check that your domain (localhost:3000) is added to allowed origins in Descope Console</li>
              <li>Ensure no firewall/antivirus is blocking api.descope.com</li>
              <li>Try disabling browser extensions temporarily</li>
              <li>Check browser network tab for specific error details</li>
              <li>Verify your Descope project is active and not suspended</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DescopeDebugger;
